import _ from 'lodash';
import React, {FunctionComponent, useState, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Grid, makeStyles, Popover, IconButton} from '@material-ui/core';
import {ZoomInSharp} from '@material-ui/icons';
import {ProductField} from '@treez/commons/sharedTypings/product';
import {ImageAttributes} from '@treez/commons/sharedTypings';
import {Button, ButtonClass} from '@treez-ui/core/buttons';
import {CloseIcon, DeleteIcon, EditIcon} from '@treez-ui/core/icons';
import ImageGui from '~/modules/widgets/ImageGui';
import PhotoGallery from '~/modules/widgets/PhotoGallery';
import {TreezColor, treezColorMap} from '~/styles/constants';
import {selectMergeProductImages} from '~/redux/selectors/mergeSelectors';
import {updateMergeProduct} from '~/redux/actions/MergeActions';

const {IMAGES} = ProductField;

export enum ImageGalleryGridCellOption {
    EDIT = 'edit',
    SELECT = 'select',
}

const {EDIT, SELECT} = ImageGalleryGridCellOption;

interface Props {
    editOrSelect?: ImageGalleryGridCellOption;
    images?: ImageAttributes[];
}

export const ImageGalleryGridCell: FunctionComponent<Props> = (props: Props) => {
    const {editOrSelect = 'select', images} = props;

    const editOptionSelected = editOrSelect === EDIT;
    const selectOptionSelected = editOrSelect === SELECT;

    const [open, setOpen] = useState<boolean>(false);

    const selectedImages = useSelector(selectMergeProductImages, _.isEqual);

    const dispatch = useDispatch();

    const handleSelectImage = (image: ImageAttributes) => {
        if (selectOptionSelected) {
            const imageAlreadySelected = _.includes(_.map(selectedImages, 'url'), image.url);

            let newImageSet: ImageAttributes[] = [];

            if (imageAlreadySelected) {
                newImageSet = _.filter(selectedImages, (si) => {
                    return si.url !== image.url;
                });
            } else {
                newImageSet = [
                    ...selectedImages,
                    {
                        url: image.url,
                        default: selectedImages.length === 0,
                    },
                ];
            }

            dispatch(updateMergeProduct(IMAGES, newImageSet));
        }
    };

    const handleSelectAll = (checked: boolean) => {
        let newImageSet: ImageAttributes[] = [];

        if (selectOptionSelected) {
            if (checked) {
                if (selectedImages.length === 0) {
                    newImageSet = images || [];
                } else {
                    const imagesDefaultedToFalse: ImageAttributes[] = _.map(images, (img) => {
                        return {
                            url: img.url,
                            default: false,
                        };
                    });
                    newImageSet = _.uniqBy(
                        [...selectedImages, ...imagesDefaultedToFalse],
                        'url'
                    ) as ImageAttributes[];
                }
            } else {
                const imageUrls = _.map(images || [], 'url');
                newImageSet = _.filter(selectedImages, (selectedImg) => {
                    return !_.includes(imageUrls, selectedImg.url);
                });
            }

            dispatch(updateMergeProduct(IMAGES, newImageSet));
        }
    };

    const onEdit = (newImageSet: ImageAttributes[]) => {
        dispatch(updateMergeProduct(IMAGES, newImageSet));
    };

    const {OFF_WHITE, GREY_DARK} = TreezColor;

    const useStyles = makeStyles({
        imagesGallery: {
            display: 'flex',
            height: '175px',
            padding: '8px',
            width: '90%',
        },
        imagesOptions: {
            alignItems: 'center',
            display: 'flex',
            height: '20px',
            justifyContent: 'space-between',
            flexDirection: 'row-reverse',
            width: '100%',
        },
        productGridCell: {
            height: '220px',
            display: 'flex',
            flexWrap: 'wrap',
            maxWidth: '100%',
        },
        popOver: {
            display: 'flex',
            flexDirection: 'column',
            height: editOptionSelected ? 'auto' : '600px',
            justifyContent: 'flex-start',
            overflow: 'hidden',
            width: '600px',
        },
        popOverHeader: {
            alignItems: 'center',
            backgroundColor: treezColorMap[OFF_WHITE],
            borderBottom: `1px solid ${treezColorMap[GREY_DARK]}`,
            display: 'flex',
            height: '30px',
        },
        popOverGalleryContainer: {
            boxSizing: 'border-box',
            display: 'flex',
            height: '90%',
            padding: '8px',
            width: '100%',
        },
        editImagesContainer: {
            padding: '8px',
        },
    });

    const {
        imagesGallery,
        imagesOptions,
        popOver,
        popOverGalleryContainer,
        productGridCell,
        popOverHeader,
        editImagesContainer,
    } = useStyles();

    const defaultImage = {
        url: 'https://assets.treez.io/bg/default_store_thumbnail.svg',
        default: true,
    };

    const imagesToRender = images == null || _.size(images) === 0 ? [defaultImage] : images;

    const cellRef = useRef<HTMLDivElement>(null);

    return (
        <Grid container item className={productGridCell} ref={cellRef} justifyContent="center">
            <Popover anchorEl={cellRef.current} open={open}>
                <Grid className={popOver}>
                    <Grid className={popOverHeader}>
                        <IconButton onClick={() => setOpen(false)}>
                            <CloseIcon color={treezColorMap[GREY_DARK]} />
                        </IconButton>
                    </Grid>
                    {selectOptionSelected && (
                        <Grid className={popOverGalleryContainer}>
                            <PhotoGallery
                                images={imagesToRender}
                                onSelectAll={handleSelectAll}
                                onSelectImage={handleSelectImage}
                                selectedImages={selectedImages}
                            />
                        </Grid>
                    )}
                    {editOptionSelected && (
                        <Grid className={editImagesContainer}>
                            <ImageGui setUploadedImages={onEdit} uploadedImages={images || []} />
                        </Grid>
                    )}
                </Grid>
            </Popover>
            <Grid item sm={12} className={imagesGallery}>
                <PhotoGallery images={imagesToRender} />
            </Grid>

            <Grid item sm={12} className={imagesOptions}>
                {selectOptionSelected && _.size(images) > 0 && (
                    <Grid>
                        <IconButton
                            onClick={() => {
                                setOpen(true);
                            }}
                        >
                            <ZoomInSharp />
                        </IconButton>
                    </Grid>
                )}

                {editOptionSelected && (
                    <Grid>
                        <IconButton
                            onClick={() => {
                                setOpen(true);
                            }}
                        >
                            <EditIcon />
                        </IconButton>
                    </Grid>
                )}
                {editOptionSelected && (
                    <Grid>
                        {_.size(images) > 0 && (
                            <Button
                                buttonClass={ButtonClass.QUIET}
                                icon={<DeleteIcon color={treezColorMap[TreezColor.ERROR_RED]} />}
                                onClick={() => onEdit([])}
                                text="Remove All"
                            />
                        )}
                    </Grid>
                )}
            </Grid>
        </Grid>
    );
};

export default ImageGalleryGridCell;
